import { logger } from '@/features/help/utils/logger';
import { FormStructure, FormField } from '../types/forms';

// Purpose: Validates Federal Tax ID format (XX-XXXXXXX or 9 digits)
export const validateFederalTaxId = (value: string): boolean => {
  if (!value) return false;

  const trimmedValue = value.trim();
  const digitsOnly = trimmedValue.replace(/\D/g, '');

  const federalTaxIdRegex = /^\d{2}-\d{7}$/;
  const isValid = federalTaxIdRegex.test(trimmedValue) || digitsOnly.length === 9;

  return isValid;
};

// Purpose: Formats a Federal Tax ID as XX-XXXXXXX
export const formatFederalTaxId = (value: string): string => {
  if (!value) return '';

  // Remove all non-digit characters first
  const digitsOnly = value.replace(/\D/g, '');

  // Only format if we have exactly 9 digits
  if (digitsOnly.length === 9) {
    return `${digitsOnly.substring(0, 2)}-${digitsOnly.substring(2)}`;
  }

  // If not 9 digits, return the cleaned digits (up to 9)
  return digitsOnly.substring(0, 9);
};

// Return digits only (max 9) for Federal Tax ID
export const canonicalFederalTaxId = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '').substring(0, 9);
};

// Purpose: Validates phone number format (10 digits minimum, with or without separators)
export const validatePhoneNumber = (value: string): boolean => {
  if (!value) return false;

  // Remove all non-digit characters for validation
  const digitsOnly = value.replace(/\D/g, '');

  // Phone number should have at least 10 digits
  const isValid = digitsOnly.length >= 10;

  return isValid;
};

// Purpose: Formats a phone number as XXX-XXX-XXXX
export const formatPhoneNumber = (value: string): string => {
  if (!value) return '';

  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  // Take at most 10 digits for standard US phone formatting
  const d = digitsOnly.substring(0, 10);

  if (d.length === 0 && value.length > 0) {
    // If all characters were non-digits but there was input
    return ''; // Return empty string rather than original non-digit characters
  }

  if (d.length <= 3) {
    return d;
  } else if (d.length <= 6) {
    return `${d.slice(0, 3)}-${d.slice(3)}`;
  } else {
    // 7 to 10 digits
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
};

// Return digits only (max 10) for phone numbers
export const canonicalPhoneNumber = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '').substring(0, 10);
};

// Purpose: Validates ZIP code format (exactly 5 digits)
export const validateZipCode = (value: string): boolean => {
  if (!value) return false;

  const trimmedValue = value.trim();

  // Allow only 5-digit ZIP codes
  const zipCodeRegex = /^\d{5}$/;
  const isValid = zipCodeRegex.test(trimmedValue);

  return isValid;
};

// Purpose: Validates email format
export const validateEmail = (value: string): boolean => {
  if (!value) return false;

  const trimmedValue = value.trim();

  // Check for consecutive dots, which are invalid
  if (trimmedValue.includes('..')) {
    return false;
  }

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(trimmedValue);

  return isValid;
};

// Purpose: Validates files against a list of accepted MIME types
export const validateFileTypes = (
  files: File[],
  acceptedTypes: string
): { validFiles: File[]; invalidFiles: File[] } => {
  if (!files || files.length === 0) {
    return { validFiles: [], invalidFiles: [] };
  }

  const validFiles: File[] = [];
  const invalidFiles: File[] = [];
  const acceptedMimeTypes = acceptedTypes
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t); // Remove empty strings

  // Handle the case where acceptedTypes is empty or invalid
  if (acceptedMimeTypes.length === 0) {
    logger.warn(
      'Validation',
      'validateFileTypes called with empty or invalid acceptedTypes string',
      {
        acceptedTypes,
      }
    );
    // Treat all files as invalid if no valid types are specified
    return { validFiles: [], invalidFiles: [...files] };
  }

  files.forEach((file) => {
    if (!file || !file.type) {
      invalidFiles.push(file); // Consider files without a type as invalid
      return;
    }

    const fileTypeLower = file.type.toLowerCase();

    // Check for exact match or wildcard match (e.g., 'image/*')
    const isValid = acceptedMimeTypes.some((acceptedType) => {
      if (acceptedType.endsWith('/*')) {
        // Wildcard match (e.g., 'image/*' matches 'image/png')
        return fileTypeLower.startsWith(acceptedType.slice(0, -1));
      } else {
        // Exact match
        return fileTypeLower === acceptedType;
      }
    });

    if (isValid) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file);
    }
  });

  return { validFiles, invalidFiles };
};

/**
 * Converts file extensions to MIME types for validation
 * @param acceptedTypes - Comma-separated string of file extensions (e.g., '.pdf,.jpg,.png')
 * @returns Comma-separated string of MIME types (e.g., 'application/pdf,image/jpeg,image/png')
 */
export const convertExtensionsToMimeTypes = (acceptedTypes: string | undefined): string => {
  // Handle undefined or empty acceptedTypes
  if (!acceptedTypes || acceptedTypes.trim() === '') {
    return 'application/pdf';
  }

  const extensionToMimeType: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.svg': 'image/svg+xml',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.txt': 'text/plain',
    '.csv': 'text/csv',
  };

  // If acceptedTypes already looks like MIME types, return as is
  if (acceptedTypes.includes('/')) {
    return acceptedTypes;
  }

  // Convert file extensions to MIME types
  const extensions = acceptedTypes.split(',').map((ext) => ext.trim().toLowerCase());
  const mimeTypes = extensions.map((ext) => extensionToMimeType[ext]).filter(Boolean); // Remove undefined values

  return mimeTypes.join(',');
};

/**
 * Validates ownership percentage is between 0 and 100
 */
export const validateOwnershipPercentage = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Allow empty for optional fields

  const numValue = parseFloat(value);
  return !isNaN(numValue) && numValue >= 0 && numValue <= 100;
};

/**
 * Purpose: Centralized normalization function that converts input values to canonical format
 * This ensures consistent data normalization across all form input handlers
 * @param field - The form field configuration
 * @param inputValue - The raw input value to normalize
 * @returns The normalized canonical value
 */
export const normalizeFieldValue = (field: FormField, inputValue: string): string => {
  if (!inputValue) return '';

  // Apply normalization based on field type/ID
  if (field.type === 'tax-id' || field.id === 'federalTaxId') {
    return canonicalFederalTaxId(inputValue);
  } else if (
    field.type === 'phone' ||
    field.id.toLowerCase().includes('phone') ||
    field.id === 'fax'
  ) {
    return canonicalPhoneNumber(inputValue);
  } else if (
    field.type === 'ssn' ||
    field.id.includes('SSN') ||
    field.id.includes('SocialSecurity')
  ) {
    return canonicalSSN(inputValue);
  }

  // Enforce digits-only for number fields
  if (field.type === 'number') {
    return inputValue.replace(/\D/g, '');
  }

  return inputValue;
};

/**
 * Formats SSN with dashes (XXX-XX-XXXX)
 */
export const formatSSN = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Apply formatting based on length
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 5) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else if (digits.length <= 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } else {
    // Limit to 9 digits
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  }
};

// Return digits only (max 9) for SSN values
export const canonicalSSN = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '').substring(0, 9);
};

/**
 * Validates SSN format (XXX-XX-XXXX or 9 digits)
 */
export const validateSSN = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Allow empty for optional fields

  const digits = value.replace(/\D/g, '');
  return digits.length === 9;
};

/**
 * Validates date of birth (must be in the past and person must be at least 18)
 */
export const validateDateOfBirth = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Allow empty for optional fields

  const date = new Date(value);
  const today = new Date();

  // Check if valid date
  if (isNaN(date.getTime())) return false;

  // Check if date is in the past
  if (date >= today) return false;

  // Check if person is at least 18 years old
  const eighteenYearsAgo = new Date();
  eighteenYearsAgo.setFullYear(today.getFullYear() - 18);

  return date <= eighteenYearsAgo;
};

/**
 * Synchronizes DOM form values with React state before validation
 * This handles browser autofill which populates DOM but doesn't trigger React events
 * Performs direct synchronous read of current DOM values
 */
export const syncDomValuesWithState = async (
  formElement: HTMLDivElement,
  structure: FormStructure,
  handleFieldValueChange: (fieldId: string, value: string | boolean) => void
): Promise<number> => {

  const inputs = formElement.querySelectorAll('input, textarea, select');
  let syncCount = 0;

  inputs.forEach((input) => {
    const htmlInput = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const fieldId = htmlInput.id;

    if (!fieldId) {
      return; // Skip inputs without IDs
    }

    // Find the corresponding field in the structure
    let correspondingField: FormField | undefined;
    for (const page of structure.pages) {
      for (const section of page.sections) {
        const field = section.fields.find((f: FormField) => f.id === fieldId);
        if (field) {
          correspondingField = field;
          break;
        }
      }
      if (correspondingField) break;
    }

    if (!correspondingField) {
      return; // Skip if field not found in structure
    }

    let domValue: string | boolean;

    // Get the appropriate value based on input type
    if (htmlInput.type === 'checkbox') {
      domValue = (htmlInput as HTMLInputElement).checked;
    } else {
      domValue = htmlInput.value;
    }

    // Compare DOM value with stored value
    const storedValue = correspondingField.value;
    const domValueStr = String(domValue);
    const storedValueStr = String(storedValue ?? '');

    // Only update if values differ (avoid unnecessary updates)
    if (domValueStr !== storedValueStr) {
      // Normalize the DOM value before storing it
      const normalizedValue =
        typeof domValue === 'string' ? normalizeFieldValue(correspondingField, domValue) : domValue;

      logger.info('Validation Utils', 'Syncing field value from DOM', {
        fieldId,
        domValue: domValueStr.substring(0, 50) + (domValueStr.length > 50 ? '...' : ''),
        normalizedValue:
          typeof normalizedValue === 'string'
            ? normalizedValue.substring(0, 50) + (normalizedValue.length > 50 ? '...' : '')
            : String(normalizedValue),
        storedValue: storedValueStr.substring(0, 50) + (storedValueStr.length > 50 ? '...' : ''),
        fieldType: correspondingField.type,
      });

      handleFieldValueChange(fieldId, normalizedValue);
      syncCount++;
    }
  });

  logger.info('Validation Utils', 'DOM synchronization completed', {
    fieldsUpdated: syncCount,
    totalInputsChecked: inputs.length,
  });

  return syncCount;
};
